import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Status from "../../enums/Status";
import IDSAR from "../../models/dsar/IDSAR";
import sarService from "../../services/sarService";
import { useEffect, useState } from "react";
import FormFieldValidation from "../../enums/FormFieldValidation";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import FloatingTextField from "../../components/formFields/floatingText/FloatingTextField";
import DropdownSelect from "../../components/formFields/dropdownSelect/DropdownSelect";
import LargeFloatingTextField from "../../components/formFields/largeFloatingText/LargeFloatingTextField";
import DatePicker from "../../components/formFields/datePicker/DatePicker";
import IStaticValue from "../../models/IStaticValue";
import { NIL } from "uuid";
import { Keywords } from "../keywordsComponent/Keywords";

interface IDSARValidation {
    purpose: FormFieldValidation;
    type: FormFieldValidation;
    custodian: FormFieldValidation;
    custodianType: FormFieldValidation;
    dataSearchStartDate: FormFieldValidation;
    dataSearchEndDate: FormFieldValidation;
    dataSearchLocations: FormFieldValidation;
    emailSearchStartDate: FormFieldValidation;
    emailSearchEndDate: FormFieldValidation;
    emailSearchAddress: FormFieldValidation;
    keywords: FormFieldValidation;
}

export default function NewSARForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        const isValid = validate(request, setValidation);

        if (!isValid) {
            return;
        }

        setStatus(Status.SAVING);

        submitSAR(request)
            .then(requestId => {
                if (!!requestId) {
                    setStatus(Status.SAVE_SUCCESS);

                    setTimeout(() => {
                        if (requestId !== NIL) {
                            navigate(`/sar/${requestId}`);
                        } else {
                            navigate("/sars");
                        }
                    }, 750);
                } else {
                    setStatus(Status.SAVE_ERROR);
                }
            })
            .catch(() => setStatus(Status.SAVE_ERROR));
    };

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
    const [custodianTypes, setCustodianTypes] = useState<IStaticValue[]>([]);
    const [searchTypes, setSearchTypes] = useState<IStaticValue[]>([]);

    const [request, setRequest] = useState<IDSAR>({
        id: NIL,
        reference: "",
        purpose: "",
        type: "",
        status: "",
        created: new Date().toISOString(),
        custodian: "",
        custodianConsented: false,
        custodianContractStart: "",
        custodianContractEnd: "",
        custodianContractType: "",
        dataSearchStart: "",
        dataSearchEnd: "",
        dataSearchLocations: "",
        emailSearchStart: "",
        emailSearchEnd: "",
        emailSearchAddress: "",
        keywords: [""]
    });

    const [validation, setValidation] = useState<IDSARValidation>({
        purpose: FormFieldValidation.NOT_VALIDATED,
        type: FormFieldValidation.NOT_VALIDATED,
        custodian: FormFieldValidation.NOT_VALIDATED,
        custodianType: FormFieldValidation.NOT_VALIDATED,
        dataSearchStartDate: FormFieldValidation.NOT_VALIDATED,
        dataSearchEndDate: FormFieldValidation.NOT_VALIDATED,
        dataSearchLocations: FormFieldValidation.NOT_VALIDATED,
        emailSearchStartDate: FormFieldValidation.NOT_VALIDATED,
        emailSearchEndDate: FormFieldValidation.NOT_VALIDATED,
        emailSearchAddress: FormFieldValidation.NOT_VALIDATED,
        keywords: FormFieldValidation.NOT_VALIDATED
    });

    useEffect(() => setIsReadOnly([Status.SAVE_SUCCESS, Status.SAVING, Status.LOADING, Status.LOAD_ERROR].includes(status)), [status]);

    useEffect(() => {
        Promise.all([
            getCustodianTypes(types => setCustodianTypes(types)),
            getSearchTypes(types => setSearchTypes(types))
        ])
            .then(() => setStatus(Status.LOAD_SUCCESS))
            .catch(() => setStatus(Status.LOAD_ERROR));
    }, []);

    const hasValidationErrors = Object.values(validation).some(v => v === FormFieldValidation.INVALID);

    const handleTypeChange = (type: string) => {
        handleInputChange("type", type);

        handleInputChange("dataSearchStart", "");
        handleInputChange("dataSearchEnd", "");
        handleInputChange("dataSearchLocations", "");

        handleInputChange("emailSearchStart", "");
        handleInputChange("emailSearchEnd", "");
        handleInputChange("emailSearchAddress", "");
    }
    
    const handleInputChange = (prop: keyof IDSAR, value: any) => {
        setRequest(r => ({ ...r, [prop]: value }));
        setValidation(validation => ({ ...validation, [prop]: FormFieldValidation.NOT_VALIDATED }));
    };

    const handleKeywordsChanged = (keywords: string[]) => {
        setRequest(r => ({ ...r, keywords: keywords }));
        setValidation(validation => ({ ...validation, keywords: FormFieldValidation.NOT_VALIDATED }));
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Row className="mt-3">
                <Col>
                    <h2 className="h3">
                        {t("dsar.form.sections.custodian.title")}
                    </h2>
                </Col>
            </Row>
            <Row>
                <Col>
                    <FloatingTextField
                        label={t("dsar.form.sections.custodian.fields.name.label")}
                        disabled={isReadOnly}
                        onChange={value => handleInputChange("custodian", value)}
                        validation={validation.custodian}
                        type="text"
                        value={request.custodian}
                        required={true}
                        />
                </Col>
            </Row>
            <Row className="gx-3">
                <Col>
                    <DatePicker
                        label={t("dsar.form.sections.custodian.fields.start.label")}
                        disabled={isReadOnly}
                        onChange={value => handleInputChange("custodianContractStart", value)}
                        validation={FormFieldValidation.NOT_VALIDATED}
                        value={request.custodianContractStart}
                        />
                </Col>
            </Row>
            <Row>
                <Col>
                    <DatePicker
                        label={t("dsar.form.sections.custodian.fields.end.label")}
                        disabled={isReadOnly}
                        onChange={value => handleInputChange("custodianContractEnd", value)}
                        validation={FormFieldValidation.NOT_VALIDATED}
                        value={request.custodianContractEnd}
                        />
                </Col>
            </Row>
            <Row className="gx-3">
                <Col>
                    <DropdownSelect 
                        title={t("dsar.form.sections.custodian.fields.type.label")}
                        items={custodianTypes}
                        value={request.custodianContractType!}
                        itemValueField="id"
                        itemLabelField="name"
                        valueLabel={custodianTypes.find(t => t.id === request.custodianContractType!)?.name!}
                        validation={validation.custodianType}
                        validationMessage={t("dsar.form.sections.custodian.fields.type.validation")}
                        disabled={isReadOnly}
                        required={true}
                        onchange={(type: IStaticValue) => handleInputChange("custodianContractType", type.id)} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Check
                        type={"checkbox"}
                        label={t("dsar.form.sections.custodian.fields.consent.label")}
                        onChange={ev => handleInputChange("custodianConsented", ev.target.checked)}
                        value={request.custodianConsented.toString()}
                        disabled={isReadOnly}
                        />
                </Col>
            </Row>

            <Row className="mt-3">
                <Col>
                    <h2 className="h3">
                        {t("dsar.form.sections.searchInfo.title")}
                    </h2>
                </Col>
            </Row>
            <Row className="gx-3">
                <Col>
                    <DropdownSelect 
                        title={t("dsar.form.sections.searchInfo.fields.type.label")}
                        items={searchTypes}
                        value={request.type!}
                        itemValueField="id"
                        itemLabelField="name"
                        valueLabel={searchTypes.find(t => t.id === request.type!)?.name!}
                        validation={validation.type}
                        validationMessage={t("dsar.form.sections.searchInfo.fields.type.validation")}
                        disabled={isReadOnly}
                        required={true}
                        onchange={(type: IStaticValue) => handleTypeChange(type.id)} />
                </Col>
            </Row>

            {
                request.type &&
                <Row>
                    <Col>
                        <Row className="gx-3">
                            <Col>
                                <DatePicker
                                    label={t("dsar.form.sections.searchInfo.fields.start.label")}
                                    disabled={isReadOnly}
                                    required={true}
                                    onChange={value => {
                                        switch (request.type) {
                                            case "1":
                                                handleInputChange("dataSearchStart", value);
                                                break;
                                            case "2":
                                                handleInputChange("emailSearchStart", value);
                                                break;
                                        }
                                    }}
                                    validation={(() => {
                                        switch (request.type) {
                                            case "1":
                                                return validation.dataSearchStartDate;
                                            case "2":
                                                return validation.emailSearchStartDate;
                                        }
                                    })()!}
                                    value={(() => {
                                        switch (request.type) {
                                            case "1":
                                                return new Date(request.dataSearchStart);
                                            case "2":
                                                return new Date(request.emailSearchStart);
                                        }
                                    })()!}
                                    />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <DatePicker
                                    label={t("dsar.form.sections.searchInfo.fields.end.label")}
                                    disabled={isReadOnly}
                                    required={true}
                                    onChange={value => {
                                        switch (request.type) {
                                            case "1":
                                                handleInputChange("dataSearchEnd", value);
                                                break;
                                            case "2":
                                                handleInputChange("emailSearchEnd", value);
                                                break;
                                        }
                                    }}
                                    validation={(() => {
                                        switch (request.type) {
                                            case "1":
                                                return validation.dataSearchEndDate;
                                            case "2":
                                                return validation.emailSearchEndDate;
                                        }
                                    })()!}
                                    value={(() => {
                                        switch (request.type) {
                                            case "1":
                                                return new Date(request.dataSearchEnd);
                                            case "2":
                                                return new Date(request.emailSearchEnd);
                                        }
                                    })()!}
                                    />
                            </Col>
                        </Row>

                        {
                            request.type === "1" &&
                            <Row>
                                <Col>
                                    <LargeFloatingTextField
                                        label={t("dsar.form.sections.searchInfo.fields.dataLocations.label")}
                                        disabled={isReadOnly}
                                        onChange={value => handleInputChange("dataSearchLocations", value)}
                                        validation={validation.dataSearchLocations}
                                        type="textarea"
                                        value={request.dataSearchLocations}
                                        height={100}
                                        required={true}
                                        />
                                </Col>
                            </Row>
                        }

                        {
                            request.type === "2" &&
                            <Row>
                                <Col>
                                    <FloatingTextField
                                        label={t("dsar.form.sections.searchInfo.fields.emailAddress.label")}
                                        disabled={isReadOnly}
                                        onChange={value => handleInputChange("emailSearchAddress", value)}
                                        validation={validation.emailSearchAddress}
                                        type="email"
                                        value={request.emailSearchAddress}
                                        required={true}
                                        />
                                </Col>
                            </Row>
                        }

                        <Row>
                            <Col>
                                <LargeFloatingTextField
                                    label={t("dsar.form.sections.searchInfo.fields.purpose.label")}
                                    disabled={isReadOnly}
                                    onChange={value => handleInputChange("purpose", value)}
                                    validation={validation.purpose}
                                    type="textarea"
                                    value={request.purpose}
                                    required={true}
                                    height={150}
                                    />
                            </Col>
                        </Row>

                        <Keywords
                            isReadOnly={isReadOnly}
                            keywords={request.keywords}
                            validation={validation.keywords}
                            onKeywordsChanged={handleKeywordsChanged}
                            />
                    </Col>
                </Row>
            }

            <Row>
                <Col>
                    {
                        hasValidationErrors &&
                        <Alert variant="danger">
                            {t("dsar.form.validation")}
                        </Alert>
                    }
                    {
                        status === Status.SAVING &&
                        <Alert variant="info">
                            {t("general.pleaseWait")}
                        </Alert>
                    }
                    {
                        status === Status.SAVE_SUCCESS &&
                        <Alert variant="success">
                            {t("dsar.success.submit")}
                        </Alert>
                    }
                    {
                        status === Status.SAVE_ERROR &&
                        <Alert variant="danger">
                            {t("dsar.errors.submit")}
                        </Alert>
                    }
                </Col>
            </Row>
            
            <Row className="justify-content-center">
                <Col xs={12} md={10} lg={6} xl={4}>
                    <div className="d-grid">
                        <Button type="submit" size="lg" variant="primary" disabled={isReadOnly}>{t("general.submit")}</Button>
                    </div>
                </Col>
            </Row>
        </Form>
    );
}

async function getCustodianTypes(setCustodianTypes: (ustodianTypes: IStaticValue[]) => void) {
    const custodianTypes = await sarService.getCustodianTypes();
    setCustodianTypes(custodianTypes);
}

async function getSearchTypes(setSearchTypes: (searchTypes: IStaticValue[]) => void) {
    const searchTypes = await sarService.getSearchTypes();
    setSearchTypes(searchTypes);
}

async function submitSAR(request: IDSAR) : Promise<string> {
    const requestId = await sarService.createSAR(request);
    return requestId;
}

function validate(
    request: IDSAR,
    setValidation: (validation: IDSARValidation) => void
) {
    const validation: IDSARValidation = {
        purpose: request.purpose.length > 0
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID,
        type: request.type && parseInt(request.type) >= 1 && parseInt(request.type) <= 3
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID,
        custodian: request.custodian.length > 0
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID,
        custodianType: !!request.custodianContractType
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID,
            keywords: request.keywords.length > 0 && request.keywords.filter(f => !f.trim()).length === 0
            ? FormFieldValidation.VALID
            : FormFieldValidation.INVALID,
        dataSearchStartDate: FormFieldValidation.NOT_VALIDATED,
        dataSearchEndDate: FormFieldValidation.NOT_VALIDATED,
        dataSearchLocations: FormFieldValidation.NOT_VALIDATED,
        emailSearchStartDate: FormFieldValidation.NOT_VALIDATED,
        emailSearchEndDate: FormFieldValidation.NOT_VALIDATED,
        emailSearchAddress: FormFieldValidation.NOT_VALIDATED
    };

    switch (request.type) {
        case "1":
            validation.dataSearchStartDate = !!request.dataSearchStart
                ? FormFieldValidation.VALID
                : FormFieldValidation.INVALID;
            
            validation.dataSearchEndDate = !!request.dataSearchEnd
                ? FormFieldValidation.VALID
                : FormFieldValidation.INVALID;

            validation.dataSearchLocations = !!request.dataSearchLocations
                ? FormFieldValidation.VALID
                : FormFieldValidation.INVALID;
            
            break;
        case "2":
            validation.emailSearchStartDate = !!request.emailSearchStart
                ? FormFieldValidation.VALID
                : FormFieldValidation.INVALID;
            
            validation.emailSearchEndDate = !!request.emailSearchEnd
                ? FormFieldValidation.VALID
                : FormFieldValidation.INVALID;

            validation.emailSearchAddress = !!request.emailSearchAddress
                ? FormFieldValidation.VALID
                : FormFieldValidation.INVALID;
                
            break;
    }

    setValidation(validation);

    return Object.values(validation).every(v => v === FormFieldValidation.VALID || v === FormFieldValidation.NOT_VALIDATED);
}